/**
 * Vaktplan on a phone (plan 0029 round 3, owner 2026-09-24): a row of days picks one day, and the
 * page lists that day. The pieces here are the phone's own; the rows, cells and cards are the
 * grid's, so every action the desktop has is still there.
 */
import type { ApiShift } from '@/api/data';
import { T, useT } from '@/i18n';
import { type Entry, ruleText } from '@/rules';
import { DN, DNL, fmtD, fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import * as Dialog from '@radix-ui/react-dialog';
import { dow } from '@timefly/core';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cardStatus } from './Card';
import { StatusIcon } from './StatusIcon';
import { dateOf, timeText, typeLabel } from './model';

const PHONE = '(max-width:560px)';

/** True at phone width; the same breakpoint the stylesheet uses for the schedule. */
const has = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

/** True at phone width, or under another max-width query (the toolbar folds at 700 px, issue #86). */
export function usePhone(query = PHONE): boolean {
  const [on, setOn] = useState(() => has() && window.matchMedia(query).matches);
  useEffect(() => {
    if (!has()) return;
    const mq = window.matchMedia(query);
    const f = () => setOn(mq.matches);
    mq.addEventListener('change', f);
    return () => mq.removeEventListener('change', f);
  }, [query]);
  return on;
}

type Sev = '' | 'd' | 'w';
// the dot is colour; the screen reader gets the same thing in words
const SEVW: Record<Sev, string> = { '': '', d: ', regelbrudd', w: ', advarsel' };

/** Uke and To uker: one button a day, today in blue, a dot under a day with a rule entry. */
export function DayStrip({
  days,
  pick,
  today,
  sev,
  onPick,
}: {
  days: string[];
  pick: string;
  today: string;
  sev: (d: string) => Sev;
  onPick: (d: string) => void;
}) {
  // two weeks do not fit a phone: the picked day scrolls into view
  const box = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the pressed button moves with pick
  useEffect(() => {
    box.current
      ?.querySelector('[aria-pressed="true"]')
      ?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
  }, [pick]);
  return (
    <div className={`pdays${days.length > 7 ? ' many' : ''}`} data-testid="day-strip" ref={box}>
      {days.map((d) => (
        <button
          type="button"
          key={d}
          data-day={d}
          aria-pressed={d === pick}
          aria-current={d === today ? 'date' : undefined}
          aria-label={`${DNL[dow(d)]} ${fmtD(d)}${SEVW[sev(d)]}`}
          className={[dow(d) >= 5 ? 'we' : '', d === today ? 'today' : ''].filter(Boolean).join(' ')}
          onClick={() => onPick(d)}
        >
          <span>{DN[dow(d)]}</span>
          <b>{Number(d.slice(8, 10))}</b>
          <i className={`k ${sev(d)}`} />
        </button>
      ))}
    </div>
  );
}

/** Måned: one cell a day with the number of shifts and the same dot; a tap shows that day below. */
export function MonthCal({
  days,
  pick,
  today,
  sev,
  count,
  onPick,
}: {
  days: string[];
  pick: string;
  today: string;
  sev: (d: string) => Sev;
  count: (d: string) => number;
  onPick: (d: string) => void;
}) {
  const lead = days.length ? dow(days[0] as string) : 0;
  return (
    <div className="pcal" data-testid="month-cal">
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <span className="h" key={`h${i}`}>
          {DN[i]}
        </span>
      ))}
      {/* the days before the 1st in its first week */}
      {['b0', 'b1', 'b2', 'b3', 'b4', 'b5'].slice(0, lead).map((k) => (
        <span key={k} />
      ))}
      {days.map((d) => (
        <button
          type="button"
          key={d}
          data-day={d}
          aria-pressed={d === pick}
          aria-current={d === today ? 'date' : undefined}
          aria-label={`${DNL[dow(d)]} ${fmtD(d)}, ${count(d)} ${T('vakter')}${SEVW[sev(d)]}`}
          className={[dow(d) >= 5 ? 'we' : '', d === today ? 'today' : ''].filter(Boolean).join(' ')}
          onClick={() => onPick(d)}
        >
          <b>{Number(d.slice(8, 10))}</b>
          <span>
            {count(d)} <i className={`k ${sev(d)}`} />
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * A tap on a card on a phone: there is no hover to read the marks by, so the sheet says them in
 * words, with the rules, and holds the card menu's actions and the way into selecting several.
 */
export function ShiftSheet({
  s,
  entries,
  menu,
  onPick,
  onClose,
}: {
  s: ApiShift | null;
  entries: Entry[];
  menu: { key: string; label: ReactNode; onSelect: () => void; danger?: boolean; disabled?: boolean }[];
  onPick: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const breach = entries.some((e) => e.sev === 'd');
  const act = (f: () => void) => () => {
    onClose();
    f();
  };
  return (
    <Dialog.Root open={s != null} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="scrim" />
        {s && (
          <Dialog.Content
            className="sheet"
            aria-describedby={undefined}
            data-testid="shift-sheet"
            style={{ '--cl': s.shiftType?.color_code || 'var(--control)' } as React.CSSProperties}
          >
            <div className="grab" aria-hidden="true" />
            <Dialog.Title asChild>
              <h3>
                <span className="strip" />
                {timeText(s)} · {s.user_id == null ? `${T('Ledig vakt')} · ` : ''}
                {typeLabel(s.shiftType) ?? T('Ikke planlagt')}
              </h3>
            </Dialog.Title>
            <p className="sub">
              {s.user ? `${fullName(s.user)} · ` : ''}
              {DNL[dow(dateOf(s))]?.toLowerCase()} {fmtD(dateOf(s))} ·{' '}
              {s.published ? T('publisert') : T('utkast')}
            </p>
            {cardStatus(s).map((m) => (
              <div className={`stl ${m.tone}`} key={m.key}>
                <StatusIcon kind={m.kind} />
                {m.text}
              </div>
            ))}
            {entries.length > 0 && (
              <div className={`stl rules ${breach ? 'd' : 'w'}`}>
                <Icon name={breach ? 'stop' : 'warn'} />
                <span>
                  {entries.map((e, i) => (
                    <span className="rl" key={`${e.code}-${i}`}>
                      {ruleText(t, e)}
                    </span>
                  ))}
                </span>
              </div>
            )}
            <div className="acts">
              {/* selecting is not destructive, so it comes before Slett */}
              {[
                ...menu.filter((m) => !m.danger),
                { key: 'pick', label: T('Merk flere'), onSelect: onPick },
                ...menu.filter((m) => m.danger),
              ].map((m) => (
                <button
                  type="button"
                  key={m.key}
                  className={'danger' in m && m.danger ? 'dgr' : ''}
                  disabled={('disabled' in m && m.disabled) || false}
                  onClick={act(m.onSelect)}
                >
                  {m.label}
                </button>
              ))}
              {/* a way out that is a control, for a reader that cannot tap the overlay */}
              <Dialog.Close asChild>
                <button type="button">{T('Lukk')}</button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        )}
      </Dialog.Portal>
    </Dialog.Root>
  );
}
